<script>
  import { appStatus, pushNotice } from "../stores/ui.js";
  import { onMount } from "svelte";
  import {
    initDB,
    getAllFromStore,
    getVideosByChannel,
  } from "../lib/db/indexeddb.js";
  import { scanState } from "../stores/scan.js";
  import { licenseState } from "../stores/license.js";
  import { authState } from "../stores/auth.js";

  let channels = [];
  let videos = [];
  let selectedChannel = null;
  let query = "";
  let sortKey = "videos";
  let sortDir = "desc";
  let videoSortKey = "date";
  let videoSortDir = "desc";
  let isLoading = false;
  let onlyWithVideos = false;
  let visibleChannels = 120;

  onMount(async () => {
    appStatus.set({
      primary: "Library",
      secondary: "Search local channels & videos from IndexedDB",
    });
    await loadChannels();
  });

  async function loadChannels() {
    isLoading = true;
    try {
      const db = await initDB();
      channels = await getAllFromStore(db, "channels");
      visibleChannels = 120;
    } catch (error) {
      pushNotice(error.message || "Failed to load channels.", "error");
    } finally {
      isLoading = false;
    }
  }

  async function openChannel(channel) {
    selectedChannel = channel;
    query = "";
    try {
      const db = await initDB();
      const items = await getVideosByChannel(db, channel.id);
      videos = items;
    } catch (error) {
      pushNotice(error.message || "Failed to load videos.", "error");
    }
  }

  function backToChannels() {
    selectedChannel = null;
    videos = [];
  }

  function loadMoreChannels() {
    visibleChannels += 120;
  }

  function setSort(key) {
    if (sortKey === key) {
      sortDir = sortDir === "asc" ? "desc" : "asc";
      return;
    }
    sortKey = key;
    sortDir = "desc";
  }

  function setVideoSort(key) {
    if (videoSortKey === key) {
      videoSortDir = videoSortDir === "asc" ? "desc" : "asc";
      return;
    }
    videoSortKey = key;
    videoSortDir = "desc";
  }

  function toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function formatNumber(value) {
    const num = toNumber(value);
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
    return `${num}`;
  }

  function formatDate(value) {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("de-DE");
  }

  function resolveChannelThumb(channel) {
    return (
      channel.thumbnailUrl ||
      channel.thumbnail ||
      channel.thumbnails?.medium?.url ||
      channel.thumbnails?.default?.url ||
      "/placeholder-channel.svg"
    );
  }

  function resolveVideoThumb(video) {
    return (
      video.thumbnailUrl ||
      video.thumbnail ||
      video.thumbnails?.medium?.url ||
      video.thumbnails?.high?.url ||
      (video.videoId
        ? `https://i.ytimg.com/vi/${video.videoId}/hqdefault.jpg`
        : "") ||
      (video.id ? `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg` : "") ||
      "/placeholder-video.svg"
    );
  }

  function resolveVideoId(video) {
    return video.videoId || video.id || video._id || null;
  }

  function handleImgError(event, fallback) {
    const img = event.currentTarget;
    if (img.dataset.fallbackApplied === "true") return;
    img.dataset.fallbackApplied = "true";
    img.src = fallback;
  }

  function openVideo(video) {
    const id = resolveVideoId(video);
    if (!id) return;
    window.open(`https://www.youtube.com/watch?v=${id}`, "_blank", "noopener");
  }

  $: filteredChannels = channels
    .filter((c) =>
      !query
        ? true
        : `${c.title || ""} ${c.description || ""}`
            .toLowerCase()
            .includes(query.toLowerCase()),
    )
    .filter((c) => (onlyWithVideos ? toNumber(c.videoCount) > 0 : true))
    .sort((a, b) => {
      const factor = sortDir === "asc" ? 1 : -1;
      if (sortKey === "videos")
        return (toNumber(a.videoCount) - toNumber(b.videoCount)) * factor;
      if (sortKey === "views")
        return (toNumber(a.viewCount) - toNumber(b.viewCount)) * factor;
      if (sortKey === "subscribers")
        return (toNumber(a.subscribers) - toNumber(b.subscribers)) * factor;
      if (sortKey === "latest")
        return (
          (new Date(a.lastVideoDate || 0).getTime() -
            new Date(b.lastVideoDate || 0).getTime()) *
          factor
        );
      return 0;
    });

  $: visibleChannelSlice = filteredChannels.slice(0, visibleChannels);

  $: filteredVideos = videos
    .filter((v) =>
      !query
        ? true
        : `${v.title || ""}`.toLowerCase().includes(query.toLowerCase()),
    )
    .sort((a, b) => {
      const factor = videoSortDir === "asc" ? 1 : -1;
      if (videoSortKey === "views")
        return (toNumber(a.viewCount) - toNumber(b.viewCount)) * factor;
      if (videoSortKey === "title")
        return a.title?.localeCompare(b.title || "") * factor;
      return (
        (new Date(a.publishedAt || 0).getTime() -
          new Date(b.publishedAt || 0).getTime()) *
        factor
      );
    });
</script>

{#if !$authState.isAuthenticated || !$licenseState.active}
  <section class="panel">
    <div class="panel-title">Paywall</div>
    <p>
      You need an active plan to browse the library. Please log in and activate
      your license.
    </p>
  </section>
{:else}
  <section class="title">
    <h1>YouTube Archiv-Suche</h1>
  </section>

  <section class="stats">
    <div class="stat">Kanäle: <strong>{channels.length}</strong></div>
    <div class="stat">
      Videos: <strong>{formatNumber(videos.length || 0)}</strong>
    </div>
    <div class="stat">
      Letzter Scan: <strong>{$scanState.lastScanAt || "—"}</strong>
    </div>
    <div class="actions">
      <button class="pill">Scan starten</button>
      <button
        class={`pill ${onlyWithVideos ? "active" : ""}`}
        on:click={() => (onlyWithVideos = !onlyWithVideos)}
      >
        Only Subs
      </button>
    </div>
  </section>

  <section class="search-row">
    <div class="search">
      <input
        placeholder={selectedChannel ? "Suche Videos…" : "Suche Kanäle…"}
        bind:value={query}
      />
      {#if query}
        <button class="icon" on:click={() => (query = "")}>×</button>
      {/if}
    </div>
    <button class="primary" on:click={() => (query = query.trim())}
      >Suchen</button
    >
    <button class="ghost" on:click={() => (query = "")}>Alle anzeigen</button>
  </section>

  <section class="help">
    Beispiele: tutor python (ODER) · +tutor +python (UND) · -anfänger
    (AUSSCHLIESSEN) · "machine learning" (GENAUER AUSDRUCK)
  </section>

  {#if !selectedChannel}
    <section class="sort">
      <span>Kanäle sortieren nach:</span>
      <div class="sort-pills">
        <button
          class:active={sortKey === "videos"}
          on:click={() => setSort("videos")}>Videos</button
        >
        <button
          class:active={sortKey === "views"}
          on:click={() => setSort("views")}>Views</button
        >
        <button
          class:active={sortKey === "subscribers"}
          on:click={() => setSort("subscribers")}>Abonnenten</button
        >
        <button
          class:active={sortKey === "latest"}
          on:click={() => setSort("latest")}>Datum</button
        >
      </div>
    </section>

    <section class="grid">
      {#if isLoading}
        <div class="empty">Lade Kanäle…</div>
      {:else if filteredChannels.length === 0}
        <div class="empty">Keine Kanäle gefunden.</div>
      {:else}
        {#each visibleChannelSlice as channel}
          <button class="card" on:click={() => openChannel(channel)}>
            <div class="avatar">
              <img
                src={resolveChannelThumb(channel)}
                alt=""
                on:error={(event) =>
                  handleImgError(event, "/placeholder-channel.svg")}
                loading="lazy"
                decoding="async"
              />
            </div>
            <div class="card-title">{channel.title}</div>
            <div class="card-meta">
              {formatNumber(channel.videoCount)} Videos
            </div>
            <div class="card-sub">
              {formatNumber(channel.subscribers)} Abonnenten
            </div>
          </button>
        {/each}
      {/if}
    </section>
    {#if filteredChannels.length > visibleChannels}
      <div class="load-more">
        <button class="ghost" on:click={loadMoreChannels}>Mehr laden</button>
        <span class="hint">{visibleChannels} von {filteredChannels.length}</span
        >
      </div>
    {/if}
  {:else}
    <section class="video-header">
      <div class="video-title-row">
        <button class="back" on:click={backToChannels}>← Kanäle</button>
        <div class="video-title">
          {filteredVideos.length} Videos von "{selectedChannel.title}"
        </div>
      </div>
      <div class="video-sort">
        <span>Sortieren nach:</span>
        <div class="sort-pills">
          <button
            class:active={videoSortKey === "date"}
            on:click={() => setVideoSort("date")}>Datum</button
          >
          <button
            class:active={videoSortKey === "views"}
            on:click={() => setVideoSort("views")}>Views</button
          >
          <button
            class:active={videoSortKey === "title"}
            on:click={() => setVideoSort("title")}>Titel</button
          >
        </div>
      </div>
    </section>

    <section class="video-grid">
      {#if filteredVideos.length === 0}
        <div class="empty">Keine Videos gefunden.</div>
      {:else}
        {#each filteredVideos as video}
          <button class="video-card" on:click={() => openVideo(video)}>
            <div class="thumb">
              <img
                src={resolveVideoThumb(video)}
                alt=""
                on:error={(event) =>
                  handleImgError(
                    event,
                    resolveVideoId(video)
                      ? `https://i.ytimg.com/vi/${resolveVideoId(video)}/hqdefault.jpg`
                      : "/placeholder-video.svg"
                  )}
                loading="lazy"
                decoding="async"
              />
            </div>
            <div class="video-body">
              <div class="video-card-title">{video.title}</div>
              <div class="video-card-meta">
                <span>{formatDate(video.publishedAt)}</span>
                <span>· {formatNumber(video.viewCount || 0)} Aufrufe</span>
              </div>
            </div>
          </button>
        {/each}
      {/if}
    </section>
  {/if}
{/if}

<style>
  .title {
    text-align: center;
    margin-bottom: 1.5rem;
  }

  h1 {
    margin: 0;
    font-size: clamp(2rem, 3.2vw, 3rem);
  }

  .stats {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.5rem;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(31, 41, 51, 0.08);
  }

  .stat {
    color: var(--muted);
  }

  .stat strong {
    color: var(--ink);
  }

  .actions {
    display: flex;
    gap: 0.75rem;
  }

  .pill {
    border: none;
    border-radius: 999px;
    padding: 0.5rem 1.1rem;
    background: rgba(31, 41, 51, 0.1);
    cursor: pointer;
    font-weight: 600;
  }

  .pill.active {
    background: var(--ink);
    color: #fff;
  }

  .search-row {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 1rem;
    align-items: center;
    margin-top: 1.5rem;
  }

  .search {
    background: #fff;
    border-radius: 999px;
    padding: 0.75rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    border: 1px solid rgba(31, 41, 51, 0.08);
  }

  .search input {
    border: none;
    outline: none;
    flex: 1;
    font-size: 0.95rem;
  }

  .search .icon {
    border: none;
    background: rgba(31, 41, 51, 0.1);
    width: 28px;
    height: 28px;
    border-radius: 999px;
    cursor: pointer;
  }

  .primary {
    background: #e53935;
    color: #fff;
    border: none;
    border-radius: 999px;
    padding: 0.6rem 1.4rem;
    cursor: pointer;
    font-weight: 600;
  }

  .ghost {
    background: rgba(31, 41, 51, 0.08);
    border: none;
    border-radius: 999px;
    padding: 0.6rem 1.2rem;
    cursor: pointer;
    font-weight: 600;
  }

  .help {
    margin-top: 0.75rem;
    color: var(--muted);
    font-size: 0.85rem;
  }

  .sort {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 1.5rem;
  }

  .sort-pills {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .sort-pills button {
    border: none;
    background: rgba(31, 41, 51, 0.08);
    padding: 0.4rem 0.9rem;
    border-radius: 999px;
    cursor: pointer;
    font-weight: 600;
  }

  .sort-pills button.active {
    background: var(--ink);
    color: #fff;
  }

  .grid {
    margin-top: 1.5rem;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 1.25rem;
  }

  .card {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(31, 41, 51, 0.08);
    border-radius: 18px;
    padding: 1rem;
    display: grid;
    gap: 0.5rem;
    justify-items: center;
    text-align: center;
    cursor: pointer;
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;
  }

  .card:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 24px rgba(31, 41, 51, 0.12);
  }

  .avatar {
    width: 82px;
    height: 82px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid rgba(31, 41, 51, 0.1);
  }

  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .card-title {
    font-weight: 600;
  }

  .card-meta,
  .card-sub {
    font-size: 0.85rem;
    color: var(--muted);
  }

  .video-header {
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .video-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }

  .video-title {
    font-weight: 600;
  }

  .back {
    border: none;
    background: rgba(31, 41, 51, 0.08);
    border-radius: 999px;
    padding: 0.4rem 0.9rem;
    cursor: pointer;
  }

  .video-sort {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .video-grid {
    margin-top: 1.5rem;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 1.25rem;
  }

  .video-card {
    border: none;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 18px;
    padding: 0;
    overflow: hidden;
    cursor: pointer;
    text-align: left;
    transition:
      transform 0.2s ease,
      box-shadow 0.2s ease;
  }

  .video-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 24px rgba(31, 41, 51, 0.12);
  }

  .thumb img {
    width: 100%;
    height: 160px;
    object-fit: cover;
  }

  .video-body {
    padding: 0.9rem 1rem 1rem;
  }

  .video-card-title {
    font-weight: 600;
    margin-bottom: 0.4rem;
  }

  .video-card-meta {
    color: var(--muted);
    font-size: 0.85rem;
  }

  .load-more {
    margin-top: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    justify-content: center;
  }

  .empty {
    color: var(--muted);
    font-size: 0.95rem;
  }

  @media (max-width: 900px) {
    .search-row {
      grid-template-columns: 1fr;
    }

    .actions {
      flex-wrap: wrap;
    }

    .stats {
      flex-direction: column;
      align-items: flex-start;
    }

    .video-title-row {
      flex-direction: column;
      align-items: flex-start;
    }

    .video-sort {
      flex-direction: column;
      align-items: flex-start;
    }
  }
</style>
